import { t } from '@lingui/core/macro';
import { isEmpty } from 'lodash';
import type { TradeProductAttributes } from 'types/api/groups/trades';
import type { MasterTradeProductAttributes } from 'types/api/labs/lab_trade';
import { validatePhoneNumber } from 'utils/phoneUtils';
import {
  isCipFormatValid,
  isEmailValid,
  isRppsValid,
  isSirenValid,
  isSiretValid,
  isUrlValid,
} from 'utils/validations';
import * as Yup from 'yup';

export const urlValidator = (formFieldKey: string, isRequired?: boolean) =>
  Yup.string().test(
    formFieldKey,
    t`Le format de l’url est incorrect`,
    (url) => (!isRequired || url !== undefined) && (!url || isUrlValid(url))
  );

export const emailValidator = (formFieldKey: string, isRequired: boolean = true) =>
  Yup.string().test(
    formFieldKey,
    t`Le format de l’adresse e-mail est incorrect (pas d’espaces, pas de lettres majuscules, pas de caractères spéciaux).`,
    (email) => (!isRequired || email !== undefined) && (!email || isEmailValid(email))
  );

export const phoneNumberValidator = (formFieldKey: string) =>
  Yup.string().test(
    formFieldKey,
    t`Le format du numéro de téléphone est incorrect`,
    (verifiedPhone) => (verifiedPhone ? validatePhoneNumber(verifiedPhone) : true)
  );

export const cipValidator = (formFieldKey: string) =>
  Yup.string().test(formFieldKey, t`Doit être composé de 7 chiffres`, (cip) =>
    cip ? isCipFormatValid(cip) : true
  );

export const sirenValidator = (formFieldKey: string) =>
  Yup.string().test(formFieldKey, t`Doit être composé de 9 chiffres`, (siren) =>
    siren ? isSirenValid(siren) : true
  );

export const siretValidator = (formFieldKey: string) =>
  Yup.string().test(formFieldKey, t`Doit être composé de 14 chiffres`, (siret) =>
    siret ? isSiretValid(siret) : true
  );

export const rppsValidator = (formFieldKey: string) =>
  Yup.string().test(formFieldKey, t`L’identifiant RPPS doit comporter 11 chiffres.`, (rpps) =>
    rpps ? isRppsValid(rpps) : true
  );

export const requiredString = (errorMessage = t`Veuillez renseigner ce champ`) =>
  Yup.string().trim().required(errorMessage);

export const nonNegativeNumber = () =>
  Yup.number().typeError(t`Doit être un nombre`).min(0, t`La valeur ne peut pas être négative`);

export const positiveNumber = () =>
  Yup.number().typeError(t`Doit être un nombre`).moreThan(0, t`La valeur doit être supérieure à 0`);

export const requirementValidator = (
  errorMessage = t`Vous devez ajouter au moins une demande de preuve.`,
  // The V3 lab proofs step edits compensation in its own step, so it doesn't require it here.
  withCompensation = true
) => {
  const requirementShape = withCompensation
    ? Yup.object().shape({
        compensation: Yup.object().shape({
          compensationType: Yup.string().nullable().required(),
        }),
      })
    : Yup.object().shape({});

  return Yup.array().of(requirementShape).min(1, errorMessage);
};

export const promotionTypeValidator = () =>
  requiredString().test('promotion-type', function (value) {
    if (value !== 'multi_bri' && value !== 'multi_recommended_price') return true;

    const products:
      | Pick<
          TradeProductAttributes | MasterTradeProductAttributes,
          'compensation_in_cents' | 'recommended_price_in_cents'
        >[]
      | undefined = this.options.context?.products;
    const isMultiBri = value === 'multi_bri';

    const error = isMultiBri
      ? this.createError({
          message: t`Vous devez remplir le détail des BRI variables par EAN dans le tableau des produits.`,
        })
      : this.createError({
          message: t`Vous devez remplir le détail des prix de vente conseillés variables par EAN dans le tableau des produits.`,
        });

    if (isEmpty(products)) return error;

    if (isMultiBri) {
      const hasProductWithCompensation = products?.some(
        (product) => !!product.compensation_in_cents
      );

      return hasProductWithCompensation || error;
    }

    const hasProductWithoutRecommendedPrice = products?.some(
      (product) => !product.recommended_price_in_cents
    );

    return !hasProductWithoutRecommendedPrice || error;
  });
